import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import service from '../api/ApiRoutes';

const initialState = {
    histories: [],
    history: null,
    totalHistories: 0,
    totalPages: 1,
    currentPage: 1,
    status: 'idle',
    error: null,
    successMessage: '',
    searchQuery: '',
};

// Create a new history record
export const createHistory = createAsyncThunk(
    'history/createHistory',
    async (historyData, thunkAPI) => {
        try {
            const response = await service.createHistory(historyData);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// Fetch history records with search and pagination
export const fetchHistories = createAsyncThunk(
    'history/fetchHistories',
    async ({ page, searchQuery }, thunkAPI) => {
        try {
            const response = await service.getHistories({ page, searchTerm: searchQuery });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

// Fetch all history records (without pagination)
export const fetchAllHistories = createAsyncThunk(
    'history/fetchAllHistories',
    async (config, thunkAPI) => {
        try {
            const response = await service.getAllHistory(config);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// Fetch a single history record by ID
export const fetchHistoryById = createAsyncThunk(
    'history/fetchHistoryById',
    async (historyId, thunkAPI) => {
        try {
            const response = await service.getHistoryById(historyId);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// Update a history record
export const updateHistory = createAsyncThunk(
    'history/updateHistory',
    async ({ id, historyData, config }, thunkAPI) => {
        try {
            const response = await service.updateHistory(id, historyData, config);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// Delete a history record
export const deleteHistory = createAsyncThunk(
    'history/deleteHistory',
    async (historyId, thunkAPI) => {
        try {
            await service.deleteHistory(historyId);
            return historyId;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// Slice
const historySlice = createSlice({
    name: 'history',
    initialState,
    reducers: {
        resetState(state) {
            state.error = null;
            state.successMessage = '';
            state.status = 'idle';
        },
        setSearchQuery(state, action) {
            state.searchQuery = action.payload;
        },
        setCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        removeHistory(state, action) {
            state.histories = state.histories.filter(h => h._id !== action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createHistory.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(createHistory.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.histories.push(action.payload);
                state.successMessage = 'History record created successfully!';
            })
            .addCase(createHistory.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            .addCase(fetchHistories.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchHistories.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.histories = action.payload.history;
                state.totalPages = action.payload.totalPages;
                state.totalHistories = action.payload.totalHistory;
                state.currentPage = action.payload.currentPage;
            })
            .addCase(fetchHistories.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            .addCase(fetchAllHistories.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchAllHistories.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.histories = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload?.histories || [];
            })
            .addCase(fetchAllHistories.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload?.message || 'Failed to fetch histories';
            })

            .addCase(fetchHistoryById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchHistoryById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.history = action.payload;
            })
            .addCase(fetchHistoryById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            .addCase(updateHistory.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateHistory.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const index = state.histories.findIndex(h => h._id === action.payload._id);
                if (index !== -1) {
                    state.histories[index] = action.payload;
                }
                state.successMessage = 'History record updated successfully!';
            })
            .addCase(updateHistory.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            .addCase(deleteHistory.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteHistory.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.histories = state.histories.filter(h => h._id !== action.payload);
                state.successMessage = 'History record deleted successfully!';
            })
            .addCase(deleteHistory.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { resetState, setSearchQuery, setCurrentPage, removeHistory } = historySlice.actions;
export default historySlice.reducer;
